import Filter from '@/models/filter.model';
import Product from '@/models/product.model';
import State from '@/classes/state';
import { Mode } from '@/models/config.model';

const getters = {
    /**
     * Checks if the given filter
     * is currently selected
     */
    isSelectedFilter(state: State) {
        return (filter: Filter) => {
            return state.selectedFilters.some(selectedFilter => selectedFilter.filter.id === filter.id);
        };
    },
    /**
     * Checks if the given filter
     * matches any of the currently
     * filtered products
     */
    filterMatchesAnyFilteredProduct(state: State, getters: any) {
      return (filter: Filter) => {
        const filteredProducts: Array<Product> = getters.filteredProducts;
        return filteredProducts.some(p => p.filter.indexOf(`,${filter.id},`) !== -1);
      };
    },
    /**
     * Returns an array of filtered products
     * based on the selected filters
     */
    filteredProducts(state: State) {
        const { products, selectedFilters } = state;

        if (!selectedFilters.length) {
            return products;
        }

        const filteredProducts: Array<Product> = [];
        const selectedFilterIds = selectedFilters.map(selectedFilter => selectedFilter.filter.id);

        products.forEach((product: Product) => {
            const productHasEveryFilterId = selectedFilterIds.every(filterId => product.filter.indexOf(`,${filterId},`) !== -1);

            if (productHasEveryFilterId) {
                filteredProducts.push(product);
            }
        });

        return filteredProducts;
    },
    /**
     * Sorts the filtered product array
     * based on the current productSorting
     */
    sortedProducts(state: State, getters: any) {
        const { filteredProducts } : { filteredProducts: Array<Product>} = getters;

        if (state.config.mode === Mode.MatnoReduced) {
          // keep original product sorting in mode 'MatnoReduced'
          return filteredProducts;
        }

        const sortedProducts = filteredProducts.slice(0);
        const maxIterations = 3;
        let hasValidSortProperty = false;
        let sorting: Array<{property: string, order: number}>;
        let { productSorting } = state;
        let iterationCounter = 0;

        if (!productSorting) {
          return sortedProducts;
        }
  
        do {
          iterationCounter++;

          if (iterationCounter === maxIterations) {
            // avoid infinite loops if initialSorting uses
            // an invalid sortProperty
            return sortedProducts;
          }

          const firstProduct = sortedProducts[0];
            
          if (!firstProduct) {
            // there are no products to sort
            return sortedProducts;
          }

          const productSortingArray = productSorting.split(';');
          sorting = productSortingArray.map(sortingItem => {
            const firstChar = sortingItem.slice(0, 1);
            let sortOrder: number;
            let sortPropertyIndex: number;

            switch (firstChar) {
              case "+":
                // ASC
                sortOrder = 1;
                sortPropertyIndex = 1;
                break;
              case "-":
                // DESC
                sortOrder = -1;
                sortPropertyIndex = 1;
                break;
              default:
                sortOrder = 1;
                sortPropertyIndex = 0;
                console.warn(`Sort order was not specified so default value ASC is used`);
                break;
            }

            return {
              property: sortingItem.slice(sortPropertyIndex),
              order: sortOrder,
            };
          }).filter(sortingItem => {
            const valid = firstProduct[sortingItem.property] !== undefined;

            if (!valid) {
              console.warn(
                `Invalid sort property '${sortingItem.property}'`
              );
            }

            return valid;
          });

          if (sorting.length) {
            hasValidSortProperty = true;
          } else {
            console.warn(`Falling back to initial sorting`);
            productSorting = state.config.initialSorting;
          }
        } while (!hasValidSortProperty);
  
        sortedProducts.sort((productA, productB) => {
          let sortingIndex = 0;
          let currentSorting = sorting[sortingIndex];

          while (currentSorting) {
            const product1 = currentSorting.order === 1 ? productA : productB;
            const product2 = currentSorting.order === 1 ? productB : productA;
            const compareResult = (product1[currentSorting.property] + "").localeCompare(product2[currentSorting.property] + "");

            sortingIndex++;
            currentSorting = sorting[sortingIndex];

            if (compareResult === 0) {
              continue;
            }

            return compareResult;
          }

          return 0;
        });
  
        return sortedProducts;
    },
    /**
     * Returns an chunked array of 
     * filtered and sorted products
     * based on the current productLimit
     */
    chunkedProducts(state: State, getters: any) {
        const { sortedProducts } : { sortedProducts: Array<Product>} = getters;
        const limit = state.productLimit || sortedProducts.length;
        const chunkedProducts = sortedProducts.slice(0, limit);
        return chunkedProducts;
    },
};

export default getters;
